import React, { useState, useEffect } from 'react';
import { authApi } from '../config/api';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import withAuthorization from '../components/authentication';
import Navbar from '../components/Navbar';
import {
  FaChartLine, FaMoneyBillWave, FaHandshake, FaCoins,
  FaSearch, FaCalendar, FaFileExport,
  FaExclamationTriangle, FaCar, FaTimes, FaFileDownload
} from 'react-icons/fa';
import { MdDirectionsCar } from 'react-icons/md';
import {
  PieChart, Pie, Cell,
  BarChart, Bar,
  XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const CHART_COLORS = ['#10B981','#F59E0B','#EF4444','#8B5CF6','#EC4899','#14B8A6','#F97316','#6366F1'];

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount || 0);

const formatDateIST = (date) => {
  if (!date) return '-';
  return new Date(date).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
    timeZone: 'Asia/Kolkata'
  });
};

const DATE_STORAGE_KEY = 'analyticsDateFilter';

// Date -> 'YYYY-MM-DD' in local time (toISOString would shift to UTC)
const toYMD = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// 'YYYY-MM-DD' -> local Date
const fromYMD = (s) => {
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
};

// Default range: 1st of current month to today, in IST
const getCurrentMonthRange = () => {
  const nowIST = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
  return {
    startDate: toYMD(new Date(nowIST.getFullYear(), nowIST.getMonth(), 1)),
    endDate: toYMD(nowIST),
  };
};

// Last applied date range survives a refresh (sessionStorage: this tab only, cleared when
// the tab closes); otherwise current month. "Show All" is never restored.
const loadSavedFilter = () => {
  try {
    localStorage.removeItem(DATE_STORAGE_KEY); // clear the copy older versions kept in localStorage
  } catch (e) { /* ignore */ }
  try {
    const saved = JSON.parse(sessionStorage.getItem(DATE_STORAGE_KEY));
    if (saved?.startDate && saved?.endDate) {
      return { dateRange: { startDate: saved.startDate, endDate: saved.endDate }, showAll: false };
    }
  } catch (e) { /* ignore bad storage */ }
  return { dateRange: getCurrentMonthRange(), showAll: false };
};

const saveFilter = (filter) => {
  try {
    if (filter) sessionStorage.setItem(DATE_STORAGE_KEY, JSON.stringify(filter));
    else sessionStorage.removeItem(DATE_STORAGE_KEY);
  } catch (e) { /* ignore */ }
};

// Track mobile width so charts can shrink
const useIsMobile = (breakpoint = 640) => {
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < breakpoint);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return isMobile;
};

// Duplicates are detected by the backend (isDuplicate flag); just split them for stats
const separateDuplicates = (data) => ({
  clean: data.filter(t => !t.isDuplicate),
  duplicates: data.filter(t => t.isDuplicate),
});

// Get Indian Financial Year for a date
// FY starts April 1, ends March 31
// Apr 2025 - Mar 2026 = "FY 2025-26"
const getFinancialYear = (date) => {
  const d = new Date(date);
  const month = d.getMonth(); // 0=Jan, 3=Apr
  const year = d.getFullYear();
  if (month >= 3) return `FY ${year}-${String(year + 1).slice(2)}`; // Apr-Dec
  return `FY ${year - 1}-${String(year).slice(2)}`;                  // Jan-Mar
};

// Tokens are filtered by dateOfPaymentReceived (business date), so it wins over createdAt
const getTxnDate = (t) => new Date(t.dateOfPaymentReceived || t.createdAt);
const hasValidDate = (t) => !isNaN(getTxnDate(t).getTime());

// Amount plotted in charts, per transaction type
const getChartAmount = (t, type) => {
  if (type === 'tokens' || type === 'purchase-tokens' || type === 'purchaseTokens') return t.tokenAmount || 0;
  if (type === 'deals') return (t.dealAmount || 0) - (t.anyFinalDiscountFromDealAmount || 0);
  if (type === 'purchase-deals' || type === 'purchaseDeals') return t.dealDoneAmount || 0;
  return t.tokenAmount || t.dealAmount || t.dealDoneAmount || 0;
};

// Determine chart type based on actual transaction data range
// multiple FYs → FY-wise bar, multiple months (same FY) → month-wise bar, single month → day-wise pie
const getChartType = (transactions) => {
  if (!transactions || transactions.length === 0) return 'pie';

  const dates = transactions.map(getTxnDate);
  const minDate = new Date(Math.min(...dates));
  const maxDate = new Date(Math.max(...dates));

  // Check if spans more than one financial year
  const minFY = getFinancialYear(minDate);
  const maxFY = getFinancialYear(maxDate);
  if (minFY !== maxFY) return 'year'; // multiple FYs → FY-wise bar

  // Same FY - check if spans multiple months
  const diffMonths = (maxDate.getFullYear() - minDate.getFullYear()) * 12 + (maxDate.getMonth() - minDate.getMonth());
  if (diffMonths >= 1) return 'month'; // multiple months → month-wise bar

  return 'pie'; // same month → day-wise pie
};

// Build chart data based on type
const buildChartData = (transactions, chartType, transactionType) => {
  if (!transactions.length) return [];
  const getAmount = (t) => getChartAmount(t, transactionType);
  const getDate = getTxnDate;

  if (chartType === 'pie') {
    // Group by day within same month
    const grouped = {};
    transactions.forEach((t) => {
      const d = getDate(t);
      const key = `${d.getDate()} ${MONTH_NAMES[d.getMonth()]}`;
      grouped[key] = (grouped[key] || 0) + getAmount(t);
    });
    return Object.entries(grouped)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => parseInt(a.name) - parseInt(b.name)); // day order
  }

  if (chartType === 'month') {
    // Group by month within same FY
    const grouped = {};
    transactions.forEach((t) => {
      const d = getDate(t);
      const key = `${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`;
      if (!grouped[key]) grouped[key] = { name: key, count: 0, amount: 0, fy: getFinancialYear(d) };
      grouped[key].count += 1;
      grouped[key].amount += getAmount(t);
    });
    // Sort by actual date order (Apr first in FY)
    return Object.values(grouped).sort((a, b) => {
      const [aM, aY] = [MONTH_NAMES.indexOf(a.name.split(' ')[0]), parseInt(a.name.split(' ')[1])];
      const [bM, bY] = [MONTH_NAMES.indexOf(b.name.split(' ')[0]), parseInt(b.name.split(' ')[1])];
      return aY !== bY ? aY - bY : aM - bM;
    });
  }

  if (chartType === 'year') {
    // Group by Financial Year (Apr-Mar)
    const grouped = {};
    transactions.forEach((t) => {
      const d = getDate(t);
      const fy = getFinancialYear(d);
      if (!grouped[fy]) {
        // Calculate FY start/end year for label
        const fyStartYear = parseInt(fy.split(' ')[1].split('-')[0]);
        grouped[fy] = {
          name: fy,
          label: `Apr ${fyStartYear} - Mar ${fyStartYear + 1}`, // tooltip label
          count: 0,
          amount: 0
        };
      }
      grouped[fy].count += 1;
      grouped[fy].amount += getAmount(t);
    });
    return Object.values(grouped).sort((a, b) => a.name.localeCompare(b.name));
  }

  return [];
};

// ─── Invoice button: fresh 5-minute presigned link for a stored invoice ──────
// Invoices live in a private S3 bucket; the backend signs a new link on each click.
const InvoiceButton = ({ s3Key }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const openInvoice = async () => {
    setBusy(true);
    setError('');
    // Open the tab during the click so popup blockers allow it, then point it at the link
    const tab = window.open('', '_blank');
    try {
      const { data } = await authApi.get('/invoice/view-url', { params: { key: s3Key } });
      if (tab) tab.location.href = data.url;
      else window.location.href = data.url;
    } catch (e) {
      if (tab) tab.close();
      setError(e.response?.data?.message || 'Could not get the invoice link');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col items-start">
      <button onClick={openInvoice} disabled={busy}
        className="flex items-center gap-2 px-5 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium transition text-sm disabled:opacity-60">
        <FaFileDownload size={14} /> {busy ? 'Getting invoice...' : 'Download Invoice'}
      </button>
      <span className="text-xs text-gray-400 mt-1">{error ? <span className="text-red-600">{error}</span> : 'Link valid for 5 minutes'}</span>
    </div>
  );
};

// ─── Read-Only Detail Modal - Shows ALL fields from DB as-is ─────────────────
const DetailModal = ({ transaction, type, onClose }) => {
  if (!transaction) return null;

  const isAmountField = (key) =>
    ['tokenAmount','dealDoneAmount','dealAmount','fairMarketValue','approxDealAmount',
     'totalAmountGotFromCustomerTillNowIncludingToken','amountComeFromLoan','RtoCharge',
     'anyFinalDiscountFromDealAmount','CxBankPaidAmount','LoanPaymentAmount','DueAmount',
     'challanAmount'].includes(key);

  const isDateField = (key) =>
    ['dateOfPaymentReceived','createdAt','updatedAt'].includes(key);

  const skipFields = ['_id','__v','isDuplicate','duplicateOf','dateAssigned','submissionHash','s3_key'];

  // Format key to readable label
  const formatKey = (key) => {
    return key
      .replace(/([A-Z])/g, ' $1')
      .replace(/_/g, ' ')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  };

  // Get all fields from DB excluding internal ones
  const entries = Object.entries(transaction).filter(([key]) => !skipFields.includes(key));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-5 border-b bg-gradient-to-r from-blue-600 to-purple-600 rounded-t-2xl sticky top-0">
          <div>
            <h2 className="text-xl font-bold text-white">{transaction.carTitle || 'Transaction Details'}</h2>
            <p className="text-blue-100 text-sm mt-0.5">{transaction.carRegistrationNumber} · {type.replace('-', ' ').toUpperCase()}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-full bg-white bg-opacity-20 hover:bg-opacity-30 text-white transition">
            <FaTimes size={18} />
          </button>
        </div>

        {/* ALL Fields from DB */}
        <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {entries.map(([key, value]) => {
            if (value === undefined || value === null || value === '') return null;
            let displayValue = String(value);
            if (isDateField(key)) displayValue = formatDateIST(value);
            else if (isAmountField(key)) displayValue = formatCurrency(value);
            else if (typeof value === 'object') displayValue = JSON.stringify(value);

            // Special handling: dealAmount in deals type → show net + strikethrough
            if (key === 'dealAmount' && type === 'deals') {
              const discount = transaction.anyFinalDiscountFromDealAmount || 0;
              const net = (value || 0) - discount;
              return (
                <div key={key} className="rounded-lg p-3 bg-blue-50">
                  <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-1">Deal Amount</p>
                  <p className="font-bold text-sm text-blue-700">{formatCurrency(net)}</p>
                  {discount > 0 && (
                    <p className="text-xs text-gray-400 line-through mt-0.5">{formatCurrency(value)} (original)</p>
                  )}
                </div>
              );
            }

            // Skip anyFinalDiscountFromDealAmount - already shown in dealAmount
            if (key === 'anyFinalDiscountFromDealAmount' && type === 'deals') {
              return (
                <div key={key} className="rounded-lg p-3 bg-red-50">
                  <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-1">Discount Applied</p>
                  <p className="font-bold text-sm text-red-600">- {formatCurrency(value)}</p>
                </div>
              );
            }

            return (
              <div key={key} className={`rounded-lg p-3 ${isAmountField(key) ? 'bg-blue-50' : isDateField(key) ? 'bg-purple-50' : 'bg-gray-50'}`}>
                <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-1">{formatKey(key)}</p>
                <p className={`font-semibold text-sm ${isAmountField(key) ? 'text-blue-700' : isDateField(key) ? 'text-purple-700' : 'text-gray-900'}`}>
                  {displayValue}
                </p>
              </div>
            );
          })}
        </div>

        <div className="p-5 flex items-start justify-between gap-3 border-t">
          {/* Only records with a stored invoice in S3 get the button */}
          {transaction.s3_key ? <InvoiceButton s3Key={transaction.s3_key} /> : <span />}
          <button onClick={onClose} className="px-6 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 font-medium transition text-sm">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Smart Chart ─────────────────────────────────────────────────────────────
const SmartChart = ({ transactions, transactionType, compact = false }) => {
  const isMobile = useIsMobile();
  // Records without a usable date would break the range check, so leave them out of the chart
  const dated = (transactions || []).filter(hasValidDate);
  const chartType = getChartType(dated);
  const data = buildChartData(dated, chartType, transactionType);
  if (!data.length) return null;

  const title = chartType === 'year' ? 'Financial Year-wise Comparison (Apr - Mar)'
    : chartType === 'month' ? 'Month-wise Comparison'
    : 'Daily Amount Distribution';

  // Custom tooltip for FY chart showing "Apr YYYY - Mar YYYY"
  const FYTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    const item = data.find(d => d.name === label);
    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm">
        <p className="font-bold text-gray-800">{label}</p>
        {item?.label && <p className="text-gray-500 text-xs mb-2">{item.label}</p>}
        {payload.map((p, i) => (
          <p key={i} style={{ color: p.color }} className="font-medium">
            {p.name === 'amount' ? formatCurrency(p.value) : `${p.value} cars`}
          </p>
        ))}
      </div>
    );
  };

  return (
    <div className={compact ? 'w-full overflow-hidden' : 'bg-white rounded-xl shadow-lg p-3 sm:p-6 mb-6 overflow-hidden'}>
      {!compact && <h3 className="text-base sm:text-xl font-bold text-gray-800 mb-2 sm:mb-4">{title}</h3>}
      <p className="text-xs text-gray-500 mb-2">{title}</p>
      <ResponsiveContainer width="100%" height={isMobile ? 240 : compact ? 260 : 320}>
        {chartType === 'pie' ? (
          <PieChart>
            <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy={isMobile ? '45%' : '50%'}
              outerRadius={isMobile ? '65%' : '75%'}
              label={isMobile ? false : ({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
              labelLine={!isMobile}>
              {data.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
            </Pie>
            <Tooltip formatter={(v) => formatCurrency(v)} />
            <Legend wrapperStyle={{ fontSize: isMobile ? 10 : 12 }} iconSize={isMobile ? 8 : 14} />
          </PieChart>
        ) : (
          <BarChart data={data} margin={isMobile ? { top: 5, right: 0, left: -10, bottom: 0 } : { top: 5, right: 20, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" tick={{ fontSize: isMobile ? 9 : 12 }} interval={isMobile ? 'preserveStartEnd' : 0} />
            <YAxis yAxisId="left" width={isMobile ? 40 : 60} tick={{ fontSize: isMobile ? 9 : 12 }}
              tickFormatter={(v) => `₹${(v/100000).toFixed(0)}L`} />
            <YAxis yAxisId="right" orientation="right" width={isMobile ? 24 : 40} tick={{ fontSize: isMobile ? 9 : 12 }} allowDecimals={false} />
            <Tooltip content={chartType === 'year' ? <FYTooltip /> : undefined}
              formatter={chartType !== 'year' ? (v, name) => [name === 'amount' ? formatCurrency(v) : `${v} cars`, name === 'amount' ? 'Amount' : 'Cars'] : undefined} />
            <Legend wrapperStyle={{ fontSize: isMobile ? 10 : 12 }} iconSize={isMobile ? 8 : 14} />
            <Bar yAxisId="right" dataKey="count" fill="#F59E0B" name="count" />
            <Bar yAxisId="left" dataKey="amount" fill="#10B981" name="amount" />
          </BarChart>
        )}
      </ResponsiveContainer>
    </div>
  );
};

// ─── Transaction Table Row ────────────────────────────────────────────────────
const TableRow = ({ transaction, type, isDuplicate, onRowClick, index }) => {
  const getAmount = () => {
    if (type === 'tokens') return (
      <div className="space-y-1">
        <div className="flex items-center gap-1">
          <span className="text-xs text-gray-500">Token:</span>
          <span className="text-green-600 font-semibold text-sm">{formatCurrency(transaction.tokenAmount)}</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="text-xs text-gray-500">Deal:</span>
          <span className="text-blue-600 font-semibold text-sm">{formatCurrency(transaction.dealDoneAmount)}</span>
        </div>
      </div>
    );
    if (type === 'purchase-tokens') return (
      <div className="space-y-1">
        <div className="flex items-center gap-1">
          <span className="text-xs text-gray-500">Token:</span>
          <span className="text-green-600 font-semibold text-sm">{formatCurrency(transaction.tokenAmount)}</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="text-xs text-gray-500">Approx:</span>
          <span className="text-blue-600 font-semibold text-sm">{formatCurrency(transaction.approxDealAmount)}</span>
        </div>
      </div>
    );
    if (type === 'deals') {
      const net = (transaction.dealAmount || 0) - (transaction.anyFinalDiscountFromDealAmount || 0);
      return (
        <div className="space-y-1">
          <span className="text-blue-600 font-semibold text-sm block">{formatCurrency(net)}</span>
          {transaction.anyFinalDiscountFromDealAmount > 0 && (
            <span className="text-gray-400 text-xs line-through">{formatCurrency(transaction.dealAmount)}</span>
          )}
        </div>
      );
    }
    // purchase-deals
    return <span className="text-blue-600 font-semibold text-sm">{formatCurrency(transaction.dealDoneAmount)}</span>;
  };

  return (
    <tr onClick={() => onRowClick(transaction)}
      className={`cursor-pointer transition-colors ${isDuplicate ? 'bg-red-200 hover:bg-red-300 border-l-4 border-red-600' : 'hover:bg-blue-50'}`}>
      <td className="px-4 py-3 text-xs text-gray-400 font-mono">{index}</td>
      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
        {formatDateIST(transaction.dateOfPaymentReceived || transaction.createdAt)}
      </td>
      <td className="px-4 py-3">
        <p className="text-sm font-semibold text-gray-900">{transaction.carTitle}</p>
        <p className="text-xs text-gray-500">{transaction.carModel}</p>
      </td>
      <td className="px-4 py-3 text-sm font-mono font-bold text-gray-900">
        {transaction.carRegistrationNumber}
        {isDuplicate && <span className="ml-1 inline-flex items-center gap-1 text-xs font-bold text-white bg-red-600 px-1.5 py-0.5 rounded-full">
          <FaExclamationTriangle size={8} /> DUP
        </span>}
      </td>
      <td className="px-4 py-3">
        <p className="text-sm font-medium text-gray-900">{transaction.customerName || transaction.ownerName}</p>
        <p className="text-xs text-gray-500">{transaction.customerMobile || transaction.ownerWhatsApp || transaction.customerMobileNumber}</p>
      </td>

      {/* Token types: 2 separate columns */}
      {type === 'tokens' && <>
        <td className="px-4 py-3 text-sm font-semibold text-green-600">{formatCurrency(transaction.tokenAmount)}</td>
        <td className="px-4 py-3 text-sm font-semibold text-blue-600">{formatCurrency(transaction.dealDoneAmount)}</td>
      </>}
      {type === 'purchase-tokens' && <>
        <td className="px-4 py-3 text-sm font-semibold text-green-600">{formatCurrency(transaction.tokenAmount)}</td>
        <td className="px-4 py-3 text-sm font-semibold text-blue-600">{formatCurrency(transaction.approxDealAmount)}</td>
      </>}
      {type === 'deals' && <>
        <td className="px-4 py-3">
          <span className="text-sm font-semibold text-blue-600 block">
            {formatCurrency((transaction.dealAmount || 0) - (transaction.anyFinalDiscountFromDealAmount || 0))}
          </span>
          {transaction.anyFinalDiscountFromDealAmount > 0 && (
            <span className="text-xs text-gray-400 line-through">{formatCurrency(transaction.dealAmount)}</span>
          )}
        </td>
      </>}
      {type === 'purchase-deals' && <>
        <td className="px-4 py-3 text-sm font-semibold text-blue-600">{formatCurrency(transaction.dealDoneAmount)}</td>
      </>}
    </tr>
  );
};

// ─── Mobile Transaction Card ──────────────────────────────────────────────────
const MobileCard = ({ transaction, type, isDuplicate, onCardClick }) => (
  <div onClick={() => onCardClick(transaction)}
    className={`rounded-xl shadow p-4 cursor-pointer active:scale-95 transition-all ${
      isDuplicate ? 'bg-red-200 border-2 border-red-600' : 'bg-white border border-gray-200'
    }`}>
    {isDuplicate && (
      <div className="inline-flex items-center gap-1 mb-2 text-white bg-red-600 px-2 py-0.5 rounded-full text-xs font-bold">
        <FaExclamationTriangle size={12} /> DUPLICATE
      </div>
    )}
    <div className="flex items-center gap-3 mb-3">
      <div className={`p-2 rounded-full ${isDuplicate ? 'bg-red-300' : 'bg-blue-100'}`}>
        <FaCar className={isDuplicate ? 'text-red-700' : 'text-blue-600'} />
      </div>
      <div>
        <p className="font-bold text-gray-900 text-sm">{transaction.carTitle}</p>
        <p className="text-xs font-bold text-gray-700">{transaction.carRegistrationNumber}</p>
      </div>
    </div>
    <div className="grid grid-cols-2 gap-2 text-xs border-t pt-2">
      <div>
        <span className="text-gray-500">Customer</span>
        <p className="font-semibold text-gray-900">{transaction.customerName || transaction.ownerName}</p>
      </div>
      <div>
        <span className="text-gray-500">
          {type === 'tokens' || type === 'purchase-tokens' ? 'Token Amount' : 'Deal Amount'}
        </span>
        {type === 'tokens' && (
          <div>
            <p className="font-bold text-green-600">Token: {formatCurrency(transaction.tokenAmount)}</p>
            <p className="font-bold text-blue-600">Deal: {formatCurrency(transaction.dealDoneAmount)}</p>
          </div>
        )}
        {type === 'purchase-tokens' && (
          <div>
            <p className="font-bold text-green-600">Token: {formatCurrency(transaction.tokenAmount)}</p>
            <p className="font-bold text-blue-600">Approx: {formatCurrency(transaction.approxDealAmount)}</p>
          </div>
        )}
        {type === 'deals' && (
          <p className="font-bold text-blue-600">
            {formatCurrency((transaction.dealAmount || 0) - (transaction.anyFinalDiscountFromDealAmount || 0))}
          </p>
        )}
        {type === 'purchase-deals' && (
          <p className="font-bold text-blue-600">{formatCurrency(transaction.dealDoneAmount)}</p>
        )}
      </div>
      <div className="col-span-2">
        <span className="text-gray-500">Date: </span>
        <span className="text-gray-700">{formatDateIST(transaction.dateOfPaymentReceived || transaction.createdAt)}</span>
      </div>
    </div>
  </div>
);

// ─── Main Analytics Component ─────────────────────────────────────────────────
const Analytics = () => {
  const [activeView, setActiveView] = useState('overview');
  const [stats, setStats] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [cleanTransactions, setCleanTransactions] = useState([]);
  const [duplicateTransactions, setDuplicateTransactions] = useState([]);
  const [overviewChartData, setOverviewChartData] = useState({}); // for overview charts
  const [loading, setLoading] = useState(false);
  const [initialFilter] = useState(loadSavedFilter);
  const [dateRange, setDateRange] = useState(initialFilter.dateRange);
  const [showAll, setShowAll] = useState(initialFilter.showAll);
  // Range being picked in the calendar; applied to dateRange only on Apply
  const [pickerRange, setPickerRange] = useState([fromYMD(initialFilter.dateRange.startDate), fromYMD(initialFilter.dateRange.endDate)]);
  const [searchTerm, setSearchTerm] = useState('');
  const [transactionType, setTransactionType] = useState('tokens');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  const buildParams = (extra = {}) => {
    const p = { limit: 10000, page: 1, ...extra };
    if (!showAll && dateRange.startDate && dateRange.endDate) {
      p.startDate = dateRange.startDate;
      p.endDate = dateRange.endDate;
    }
    if (searchTerm) p.search = searchTerm;
    return p;
  };

  const fetchStats = async () => {
    try {
      setLoading(true);
      const params = buildParams();
      const [tokensRes, ptRes, dealsRes, pdRes] = await Promise.all([
        authApi.get(`/analytics/tokens`, { params }),
        authApi.get(`/analytics/purchase-tokens`, { params }),
        authApi.get(`/analytics/deals`, { params }),
        authApi.get(`/analytics/purchase-deals`, { params }),
      ]);
      const td = separateDuplicates(tokensRes.data.data);
      const ptd = separateDuplicates(ptRes.data.data);
      const dd = separateDuplicates(dealsRes.data.data);
      const pdd = separateDuplicates(pdRes.data.data);

      setStats({
        counts: {
          tokens: td.clean.length,
          purchaseTokens: ptd.clean.length,
          deals: dd.clean.length,
          purchaseDeals: pdd.clean.length,
        },
        tokenStats: {
          totalTokenAmount: td.clean.reduce((s, t) => s + (t.tokenAmount || 0), 0),
          totalDealAmount: td.clean.reduce((s, t) => s + (t.dealDoneAmount || 0), 0),
          avgDealAmount: td.clean.length ? td.clean.reduce((s, t) => s + (t.dealDoneAmount || 0), 0) / td.clean.length : 0,
        },
        purchaseTokenStats: {
          totalTokenAmount: ptd.clean.reduce((s, t) => s + (t.tokenAmount || 0), 0),
          totalApproxDealAmount: ptd.clean.reduce((s, t) => s + (t.approxDealAmount || 0), 0),
          avgDealAmount: ptd.clean.length ? ptd.clean.reduce((s, t) => s + (t.approxDealAmount || 0), 0) / ptd.clean.length : 0,
        },
        dealStats: {
          totalDealAmount: dd.clean.reduce((s, d) => s + ((d.dealAmount || 0) - (d.anyFinalDiscountFromDealAmount || 0)), 0),
          totalAmountReceived: dd.clean.reduce((s, d) => s + (d.totalAmountGotFromCustomerTillNowIncludingToken || 0), 0),
          totalTokenAmount: dd.clean.reduce((s, d) => s + (d.tokenAmount || 0), 0),
          totalRtoCharge: dd.clean.reduce((s, d) => s + (d.RtoCharge || 0), 0),
          totalLoanAmount: dd.clean.reduce((s, d) => s + (d.amountComeFromLoan || 0), 0),
          avgDealAmount: dd.clean.length ? dd.clean.reduce((s, d) => s + ((d.dealAmount || 0) - (d.anyFinalDiscountFromDealAmount || 0)), 0) / dd.clean.length : 0,
        },
        purchaseDealStats: {
          totalDealAmount: pdd.clean.reduce((s, d) => s + (d.dealDoneAmount || 0), 0),
          totalBankPaid: pdd.clean.reduce((s, d) => s + (d.CxBankPaidAmount || 0), 0),
          avgDealAmount: pdd.clean.length ? pdd.clean.reduce((s, d) => s + (d.dealDoneAmount || 0), 0) / pdd.clean.length : 0,
        },
      });

      // Store clean data per type for overview charts
      setOverviewChartData({
        tokens: td.clean,
        purchaseTokens: ptd.clean,
        deals: dd.clean,
        purchaseDeals: pdd.clean,
      });

    } catch (e) {
      console.error('Stats error:', e);
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const endpointMap = {
        tokens: '/analytics/tokens',
        'purchase-tokens': '/analytics/purchase-tokens',
        deals: '/analytics/deals',
        'purchase-deals': '/analytics/purchase-deals',
      };
      const res = await authApi.get(`${endpointMap[transactionType]}`, { params: buildParams() });
      const data = res.data.data;
      const { clean, duplicates } = separateDuplicates(data);
      setTransactions(data);
      setCleanTransactions(clean);
      setDuplicateTransactions(duplicates);
    } catch (e) {
      console.error('Transactions error:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if ((dateRange.startDate && dateRange.endDate) || showAll) {
      if (activeView === 'overview') fetchStats();
      else fetchTransactions();
    }
  }, [activeView, dateRange.startDate, dateRange.endDate, showAll, transactionType]);

  // Open the date popup showing the currently applied range (closing without Apply discards changes)
  const openDatePopup = () => {
    setPickerRange([fromYMD(dateRange.startDate), fromYMD(dateRange.endDate)]);
    setShowFilters(true);
  };

  const handleShowAll = () => {
    setShowAll(true);
    setDateRange({ startDate: '', endDate: '' });
    setShowFilters(false);
  };

  const handleApplyFilter = () => {
    const [start, end] = pickerRange;
    if (!start) return;
    const range = { startDate: toYMD(start), endDate: toYMD(end || start) }; // single day if no end picked
    setDateRange(range);
    setPickerRange([start, end || start]);
    setShowAll(false);
    setShowFilters(false);
    saveFilter(range);
  };

  const handleReset = () => {
    const range = getCurrentMonthRange();
    setDateRange(range);
    setPickerRange([fromYMD(range.startDate), fromYMD(range.endDate)]);
    setShowAll(false);
    setSearchTerm('');
    setShowFilters(false);
    saveFilter(null);
  };

  const exportToCSV = () => {
    if (!cleanTransactions.length) return;
    const headers = Object.keys(cleanTransactions[0]).join(',');
    const rows = cleanTransactions.map(t => Object.values(t).map(v => `"${v}"`).join(',')).join('\n');
    const blob = new Blob([`${headers}\n${rows}`], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${transactionType}_${Date.now()}.csv`;
    a.click();
  };

  const totalAmount = (arr) => arr.reduce((s, t) => s + (t.tokenAmount || t.dealAmount || t.dealDoneAmount || 0), 0);

  const dateLabel = showAll
    ? transactions.length
      ? `All Data: ${formatDateIST(new Date(Math.min(...transactions.map(t => new Date(t.dateOfPaymentReceived || t.createdAt)))))} → ${formatDateIST(new Date(Math.max(...transactions.map(t => new Date(t.dateOfPaymentReceived || t.createdAt)))))}`
      : 'All Data from Database'
    : dateRange.startDate && dateRange.endDate
      ? `${dateRange.startDate} to ${dateRange.endDate}`
      : 'Select Date Range';

  const TABS = [
    { id: 'overview', label: 'Overview', icon: FaChartLine, bg: '#2563EB' },
    { id: 'tokens', label: 'Sell Tokens', icon: FaCoins, bg: '#10B981' },
    { id: 'purchase-tokens', label: 'Purchase Tokens', icon: FaMoneyBillWave, bg: '#8B5CF6' },
    { id: 'deals', label: 'Sell Deals', icon: FaHandshake, bg: '#F97316' },
    { id: 'purchase-deals', label: 'Purchase Deals', icon: MdDirectionsCar, bg: '#EC4899' },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      {/* Detail Modal */}
      {selectedTransaction && (
        <DetailModal
          transaction={selectedTransaction}
          type={transactionType}
          onClose={() => setSelectedTransaction(null)}
        />
      )}

      {/* Header */}
      <div className="bg-white shadow-md border-b-4 border-blue-500">
        <div className="max-w-7xl mx-auto px-4 py-5">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900 flex items-center gap-2">
                <FaChartLine className="text-blue-600" /> Transaction Analytics
              </h1>
              <p className="text-sm text-blue-600 font-medium mt-1 flex items-center gap-1">
                <FaCalendar size={12} /> {dateLabel}
              </p>
            </div>
            <div className="flex gap-2 flex-wrap">
              <button onClick={handleShowAll}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${showAll ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                Show All Data
              </button>
              <button onClick={openDatePopup}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700">
                <FaCalendar size={12} /> Date Filter
              </button>
            </div>
          </div>

          {showFilters && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
              onClick={() => setShowFilters(false)}>
              <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xs sm:max-w-sm" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between px-5 py-4 border-b">
                  <div>
                    <h2 className="text-lg font-bold text-gray-900">Select Date Range</h2>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {pickerRange[0] ? `${toYMD(pickerRange[0])} → ${pickerRange[1] ? toYMD(pickerRange[1]) : 'pick end date'}` : 'pick start date'}
                    </p>
                  </div>
                  <button onClick={() => setShowFilters(false)} className="p-2 rounded-full hover:bg-gray-100 text-gray-500">
                    <FaTimes size={16} />
                  </button>
                </div>
                <div className="flex justify-center p-4">
                  <DatePicker
                    inline
                    selectsRange
                    startDate={pickerRange[0]}
                    endDate={pickerRange[1]}
                    onChange={(range) => setPickerRange(range)}
                    maxDate={new Date()}
                    monthsShown={1}
                  />
                </div>
                <div className="flex gap-2 px-5 pb-5">
                  <button onClick={handleApplyFilter} disabled={!pickerRange[0]}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-50">
                    Apply
                  </button>
                  <button onClick={handleReset}
                    className="px-4 py-2 bg-gray-400 text-white rounded-lg text-sm font-semibold hover:bg-gray-500">
                    Reset
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-4 mt-5">
        <div className="flex gap-2 overflow-x-auto bg-white p-2 rounded-xl shadow">
          {TABS.map(({ id, label, icon: Icon, bg }) => (
            <button key={id}
              onClick={() => { setActiveView(id); if (id !== 'overview') setTransactionType(id); }}
              style={activeView === id ? { backgroundColor: bg } : {}}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold whitespace-nowrap transition-all ${activeView === id ? 'text-white shadow' : 'text-gray-600 hover:bg-gray-100'}`}>
              <Icon size={14} /> {label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600" />
          </div>
        ) : activeView === 'overview' && stats ? (

          /* ── OVERVIEW ── */
          <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { title: 'Sell Tokens', value: stats.counts.tokens, icon: FaCoins, color: '#10B981', id: 'tokens' },
                { title: 'Purchase Tokens', value: stats.counts.purchaseTokens, icon: FaMoneyBillWave, color: '#8B5CF6', id: 'purchase-tokens' },
                { title: 'Sell Deals', value: stats.counts.deals, icon: FaHandshake, color: '#F97316', id: 'deals' },
                { title: 'Purchase Deals', value: stats.counts.purchaseDeals, icon: MdDirectionsCar, color: '#EC4899', id: 'purchase-deals' },
              ].map(({ title, value, icon: Icon, color, id }) => (
                <div key={id} onClick={() => { setActiveView(id); setTransactionType(id); }}
                  className="bg-white rounded-xl shadow p-5 cursor-pointer hover:shadow-lg hover:scale-105 transition-all border-l-4"
                  style={{ borderColor: color }}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-gray-500 font-medium">{title}</p>
                      <p className="text-3xl font-bold text-gray-800 mt-1">{value}</p>
                    </div>
                    <div className="p-3 rounded-full" style={{ backgroundColor: `${color}20` }}>
                      <Icon size={24} style={{ color }} />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {[
                {
                  title: 'Sell Token Analytics', icon: FaCoins, color: 'green',
                  rows: [
                    ['Total Token Amount', formatCurrency(stats.tokenStats.totalTokenAmount), 'green'],
                    ['Total Deal Amount', formatCurrency(stats.tokenStats.totalDealAmount), 'blue'],
                    ['Average Deal', formatCurrency(stats.tokenStats.avgDealAmount), 'gray'],
                  ]
                },
                {
                  title: 'Purchase Token Analytics', icon: FaMoneyBillWave, color: 'purple',
                  rows: [
                    ['Total Token Amount', formatCurrency(stats.purchaseTokenStats.totalTokenAmount), 'purple'],
                    ['Total Approx Deal', formatCurrency(stats.purchaseTokenStats.totalApproxDealAmount), 'blue'],
                    ['Average Deal', formatCurrency(stats.purchaseTokenStats.avgDealAmount), 'gray'],
                  ]
                },
                {
                  title: 'Sell Deal Analytics', icon: FaHandshake, color: 'orange',
                  rows: [
                    ['Total Deal Amount', formatCurrency(stats.dealStats.totalDealAmount), 'orange'],
                    ['Amount Received', formatCurrency(stats.dealStats.totalAmountReceived), 'green'],
                    ['Token Amount', formatCurrency(stats.dealStats.totalTokenAmount), 'purple'],
                    ['RTO Charges', formatCurrency(stats.dealStats.totalRtoCharge), 'blue'],
                    ['Loan Amount', formatCurrency(stats.dealStats.totalLoanAmount), 'indigo'],
                    ['Average Deal', formatCurrency(stats.dealStats.avgDealAmount), 'gray'],
                  ]
                },
                {
                  title: 'Purchase Deal Analytics', icon: MdDirectionsCar, color: 'pink',
                  rows: [
                    ['Total Deal Amount', formatCurrency(stats.purchaseDealStats.totalDealAmount), 'pink'],
                    ['Average Deal', formatCurrency(stats.purchaseDealStats.avgDealAmount), 'gray'],
                  ]
                },
              ].map(({ title, icon: Icon, color, rows }) => (
                <div key={title} className="bg-white rounded-xl shadow p-5">
                  <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2 text-lg">
                    <Icon className={`text-${color}-500`} /> {title}
                  </h3>
                  <div className="space-y-2">
                    {rows.map(([label, val, c]) => (
                      <div key={label} className={`flex justify-between items-center p-2 bg-${c}-50 rounded-lg`}>
                        <span className="text-sm text-gray-600">{label}</span>
                        <span className={`font-bold text-${c}-600 text-sm`}>{val}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Overview Charts - Smart chart per transaction type */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-2">
              {[
                { key: 'tokens', label: 'Sell Tokens' },
                { key: 'purchaseTokens', label: 'Purchase Tokens' },
                { key: 'deals', label: 'Sell Deals' },
                { key: 'purchaseDeals', label: 'Purchase Deals' },
              ].map(({ key, label }) =>
                overviewChartData[key]?.length > 0 ? (
                  <div key={key} className="bg-white rounded-xl shadow p-3 sm:p-5 min-w-0 overflow-hidden">
                    <h3 className="font-bold text-gray-700 mb-1 text-base">{label}</h3>
                    <SmartChart transactions={overviewChartData[key]} transactionType={key} compact={true} />
                  </div>
                ) : null
              )}
            </div>

          </div>

        ) : (

          /* ── TRANSACTION DETAIL VIEW ── */
          <div>
            {/* Stats Banner */}
            <div className="grid grid-cols-3 gap-3 mb-5">
              {[
                { label: 'Clean', count: cleanTransactions.length, amount: totalAmount(cleanTransactions), bg: 'from-green-500 to-green-600', note: '✅ In Overview' },
                { label: 'Duplicates', count: duplicateTransactions.length, amount: totalAmount(duplicateTransactions), bg: 'from-red-500 to-red-600', note: '❌ Excluded' },
                { label: 'Total Records', count: transactions.length, amount: totalAmount(transactions), bg: 'from-blue-500 to-blue-600', note: 'ℹ️ All Records' },
              ].map(({ label, count, amount, bg, note }) => (
                <div key={label} className={`bg-gradient-to-br ${bg} text-white rounded-xl p-4 shadow`}>
                  <p className="text-xs opacity-80">{label}</p>
                  <p className="text-3xl font-bold">{count}</p>
                  <p className="text-xs opacity-80 mt-1">{formatCurrency(amount)}</p>
                  <p className="text-xs opacity-60 mt-1">{note}</p>
                </div>
              ))}
            </div>

            {/* Search + Export */}
            <div className="bg-white rounded-xl shadow p-4 mb-5 flex flex-col sm:flex-row gap-3">
              <div className="flex-1 relative">
                <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                <input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && fetchTransactions()}
                  placeholder="Search car, reg, customer..."
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-400" />
              </div>
              <button onClick={fetchTransactions} className="px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700">Search</button>
              <button onClick={exportToCSV} className="flex items-center gap-2 px-5 py-2 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700">
                <FaFileExport size={12} /> Export CSV
              </button>
            </div>

            {/* Smart Chart */}
            <SmartChart
              transactions={cleanTransactions}
              transactionType={transactionType}
            />

            {/* Single Table - all transactions in DB order, duplicates marked red */}
            {transactions.length > 0 && (
              <div className="bg-white rounded-xl shadow overflow-hidden mb-6">
                <div className="p-4 border-b flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <h3 className="font-bold text-gray-800">
                      All Transactions ({transactions.length})
                    </h3>
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium">
                      {cleanTransactions.length} clean
                    </span>
                    {duplicateTransactions.length > 0 && (
                      <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded-full font-medium">
                        {duplicateTransactions.length} duplicates
                      </span>
                    )}
                  </div>
                </div>

                {/* DESKTOP TABLE */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        {['#', 'Date & Time', 'Car Details', 'Registration', 'Customer',
                          ...(transactionType === 'tokens' ? ['Token Amount', 'Deal Amount'] :
                              transactionType === 'purchase-tokens' ? ['Token Amount', 'Approx Deal'] :
                              ['Deal Amount'])
                        ].map(h => (
                          <th key={h} className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {transactions.map((t, i) => (
                        <TableRow key={t._id || i} index={i + 1} transaction={t} type={transactionType} isDuplicate={!!t.isDuplicate} onRowClick={setSelectedTransaction} />
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* MOBILE CARDS */}
                <div className="md:hidden p-3 grid grid-cols-1 gap-3">
                  {transactions.map((t, i) => (
                    <MobileCard key={t._id || i} transaction={t} type={transactionType} isDuplicate={!!t.isDuplicate} onCardClick={setSelectedTransaction} />
                  ))}
                </div>
              </div>
            )}

            {!transactions.length && (
              <div className="bg-white rounded-xl shadow p-12 text-center text-gray-500">No transactions found</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default withAuthorization(Analytics, ['admin']);
